import { MutationHookOptions, QueryHookOptions } from '@apollo/client';
import {
  useApiLazyQuery,
  useApiMutation,
  useApiQuery,
  useApiUploader
} from '@common/hooks';
import { GetUserAssignedLinesResponse } from '@features/salesforce/users/types';

import {
  downloadRenewalListOfMarines,
  downloadRenewalListOfVehicles,
  getAdditionalPolicyDocuments,
  getGeoLocations,
  getMotorLovs,
  getMotorModelLovs,
  getPoliciesList,
  getPolicyBBBDetails,
  getPolicyBusinessPartners,
  getPolicyById,
  getPolicyDocuments,
  getPolicyExpat,
  getPolicyFilters,
  getPolicyFleetCertificates,
  getPolicyFleetMarineCertificates,
  getPolicyGroupMedicalCertificates,
  getPolicyMarineDetails,
  getPolicyMarineHull,
  getPolicyMedical,
  getPolicyMotor,
  getPolicyPersons,
  getPolicyPlanCovers,
  getPolicyPremiumDetails,
  getPolicyProperty,
  getPolicyQuoteById,
  getPolicyRiskDescription,
  getPolicyTravel,
  getPolicyTravelPremiumDetails,
  getPropertyDescriptionLovs,
  getUserAssignedLines,
  searchCertificatePayments,
  searchPoliciesForRenewals,
  searchPolicyPayments,
  validateVignette,
  validateVignetteExist,
  addCertificate,
  addMarineCertificate,
  initializePolicyCorrection,
  regeneratePolicyDocuments,
  uploadFleetList,
  uploadMarineFleetList
} from './services';
import { GetPolicyTravelDetailsResponse } from './types/getPolicyTravelDetails.types';
import { GetPolicyTravelPremiumDetailsResponse } from './types/getPolicyTravelPremiumDetails.types';
import {
  DownloadRenewalListOfMarinesResponse,
  DownloadRenewalListOfVehiclesResponse,
  GetAdditionalPolicyDocumentsResponse,
  GetCertificatePaymentsResponse,
  GetCertificatePaymentsVariables,
  GetDescriptionOfRiskResponse,
  GetGeoLocationsResponse,
  GetMarineHullDetailsResponse,
  GetMotorDetailsLovsResponse,
  GetMotorModelLovsResponse,
  GetMotorModelsDetailsLovsVariables,
  GetPoliciesForRenewalListResponse,
  GetPoliciesForRenewalListVariables,
  GetPoliciesListResponse,
  GetPoliciesListVariables,
  GetPolicyBBBDetailsResponse,
  GetPolicyBusinessPartnersResponse,
  GetPolicyBusinessPartnersVariables,
  GetPolicyByIdResponse,
  GetPolicyDocumentsResponse,
  GetPolicyExpatDetailsResponse,
  GetPolicyFiltersFiltersResponse,
  GetPolicyFiltersVariables,
  GetPolicyFleetMarineCertificatesResponse,
  GetPolicyFleetMarineCertificatesVariables,
  GetPolicyGroupMedicalCertificatesResponse,
  GetPolicyGroupMedicalCertificatesVariables,
  GetPolicyMarineDetailsResponse,
  GetPolicyMedicalDetailsResponse,
  GetPolicyMotorDetailsResponse,
  GetPolicyPaymentsResponse,
  GetPolicyPaymentsVariables,
  GetPolicyPersonsResponse,
  GetPolicyPolicyPlanCoversResponse,
  GetPolicyPolicyPlanCoversVariables,
  GetPolicyPremiumDetailsResponse,
  GetPolicyPremiumDetailsVariables,
  GetPolicyPropertyRiskDetailsResponse,
  GetPolicyQuoteResponse,
  GetPolicyQuoteVariables,
  GetPropertyDescriptionLovsResponse,
  GetPropertyDescriptionLovsVariables,
  InitializePolicyCorrectionResponse,
  PolicyIDVariables,
  PolicyIdVariables,
  RegeneratePolicyDocumentsResponse,
  RegeneratePolicyDocumentsVariables,
  UploadFleetListResponse,
  ValidateVignetteExistResponse,
  ValidateVignetteExistVariables,
  ValidateVignetteResponse,
  ValidateVignetteVariables,
  GetPolicyFleetCertificatesResponse,
  GetPolicyFleetCertificatesVariables
} from './types';

import { IDocument } from '../proposal';
import DataService from '@core/services/DataService';
import { ErrorLogs } from '@common/types';
import { GenericLines } from 'src/enums/line.enums';
import { getGenericLine } from '@utils/helper-utils';

const useGetPolicies = (
  options?: QueryHookOptions<GetPoliciesListResponse, GetPoliciesListVariables>
) =>
  useApiQuery<GetPoliciesListResponse, GetPoliciesListVariables>(
    getPoliciesList,
    'getPolciies',
    {
      ...options,
      fetchPolicy: 'network-only', // CHANGED from 'no-cache'
      notifyOnNetworkStatusChange: true, // ADDED
      nextFetchPolicy: 'network-only' // ADDED - ensures refetch on variable change
    },
    {
      withLoader: false
    }
  );

const useGetPolicyFilters = () =>
  useApiQuery<GetPolicyFiltersFiltersResponse, GetPolicyFiltersVariables>(
    getPolicyFilters,
    'getPolicyFilters',
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true // important!
    }
  );

const useGetPolicyById = (
  options?: QueryHookOptions<GetPolicyByIdResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyByIdResponse, PolicyIDVariables>(
    getPolicyById,
    'getPolicyById',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    }
  );

const useGetPolicyMarineDetails = (
  options?: QueryHookOptions<GetPolicyMarineDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyMarineDetailsResponse, PolicyIDVariables>(
    getPolicyMarineDetails,
    'getPolicyMarineDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyRiskDescriptionDetails = (
  options?: QueryHookOptions<GetDescriptionOfRiskResponse, PolicyIDVariables>
) =>
  useApiQuery<GetDescriptionOfRiskResponse, PolicyIDVariables>(
    getPolicyRiskDescription,
    'getPolicyRiskDescription',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyPropertyDescriptionDetails = (
  options?: QueryHookOptions<
    GetPolicyPropertyRiskDetailsResponse,
    PolicyIDVariables
  >
) =>
  useApiQuery<GetPolicyPropertyRiskDetailsResponse, PolicyIDVariables>(
    getPolicyProperty,
    'getPolicyProperty',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyBBBDetails = (
  options?: QueryHookOptions<GetPolicyBBBDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyBBBDetailsResponse, PolicyIDVariables>(
    getPolicyBBBDetails,
    'getPolicyBBBDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyMarineHullDetails = (
  options?: QueryHookOptions<GetMarineHullDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetMarineHullDetailsResponse, PolicyIDVariables>(
    getPolicyMarineHull,
    'getPolicyMarineHull',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyCovers = (
  options?: QueryHookOptions<
    GetPolicyPolicyPlanCoversResponse,
    GetPolicyPolicyPlanCoversVariables
  >
) =>
  useApiQuery<
    GetPolicyPolicyPlanCoversResponse,
    GetPolicyPolicyPlanCoversVariables
  >(
    getPolicyPlanCovers,
    'getPolicyPlanCovers',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetPolicyQuoteById = (
  options?: QueryHookOptions<GetPolicyQuoteResponse, GetPolicyQuoteVariables>
) =>
  useApiQuery<GetPolicyQuoteResponse, GetPolicyQuoteVariables>(
    getPolicyQuoteById,
    'getPolicyQuoteById',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyPersons = (
  options?: QueryHookOptions<GetPolicyPersonsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyPersonsResponse, PolicyIDVariables>(
    getPolicyPersons,
    'getPolicyPersons',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyBusinessPartners = (
  options?: QueryHookOptions<
    GetPolicyBusinessPartnersResponse,
    GetPolicyBusinessPartnersVariables
  >
) =>
  useApiLazyQuery<
    GetPolicyBusinessPartnersResponse,
    GetPolicyBusinessPartnersVariables
  >(
    getPolicyBusinessPartners,
    'getPolicyBusinessPartners',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetGeoLocations = (
  options?: QueryHookOptions<GetGeoLocationsResponse>
) =>
  useApiQuery<GetGeoLocationsResponse>(
    getGeoLocations,
    'getGeoLocations',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPropertyDescriptionLovs = (
  options?: QueryHookOptions<
    GetPropertyDescriptionLovsResponse,
    GetPropertyDescriptionLovsVariables
  >
) =>
  useApiQuery<
    GetPropertyDescriptionLovsResponse,
    GetPropertyDescriptionLovsVariables
  >(
    getPropertyDescriptionLovs,
    'getPropertyDescriptionLovs',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useRegeneratePolicyDocuments = (
  options?: QueryHookOptions<
    RegeneratePolicyDocumentsResponse,
    RegeneratePolicyDocumentsVariables
  >
) =>
  useApiMutation<
    RegeneratePolicyDocumentsResponse,
    RegeneratePolicyDocumentsVariables
  >(
    regeneratePolicyDocuments,
    'regeneratePolicyDocuments',
    {
      withLoader: true,
      successMsg: 'Policy Documents were successfully generated'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getPolicyById, getPolicyDocuments] // important!
    }
  );

const useGetPolicyDocuments = (
  options?: QueryHookOptions<GetPolicyDocumentsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyDocumentsResponse, PolicyIDVariables>(
    getPolicyDocuments,
    'getPolicyDocuments',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetAdditionalPolicyDocuments = (
  options?: QueryHookOptions<
    GetAdditionalPolicyDocumentsResponse,
    PolicyIDVariables
  >
) =>
  useApiQuery<GetAdditionalPolicyDocumentsResponse, PolicyIDVariables>(
    getAdditionalPolicyDocuments,
    'getAdditionalPolicyDocuments',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyAssignedLines = (
  options?: QueryHookOptions<GetUserAssignedLinesResponse>
) =>
  useApiQuery<GetUserAssignedLinesResponse>(
    getUserAssignedLines,
    'getProposalAssignedLines',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetPolicyPayments = (
  options?: QueryHookOptions<
    GetPolicyPaymentsResponse,
    GetPolicyPaymentsVariables
  >
) =>
  useApiLazyQuery<GetPolicyPaymentsResponse, GetPolicyPaymentsVariables>(
    searchPolicyPayments,
    'getPolicyPayments',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetCertificatePayments = (
  options?: QueryHookOptions<
    GetCertificatePaymentsResponse,
    GetCertificatePaymentsVariables
  >
) =>
  useApiLazyQuery<
    GetCertificatePaymentsResponse,
    GetCertificatePaymentsVariables
  >(
    searchCertificatePayments,
    'getCertificatePayments',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetPolicyExpatDetails = (
  options?: QueryHookOptions<GetPolicyExpatDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyExpatDetailsResponse, PolicyIDVariables>(
    getPolicyExpat,
    'getPolicyExpat',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    { withLoader: false }
  );

const useGetPolicyMedicalDetails = (
  options?: QueryHookOptions<GetPolicyMedicalDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyMedicalDetailsResponse, PolicyIDVariables>(
    getPolicyMedical,
    'getPolicyMedical',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetPolicyTravelDetails = (
  options?: QueryHookOptions<GetPolicyTravelDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyTravelDetailsResponse, PolicyIDVariables>(
    getPolicyTravel,
    'getPolicyTravel',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetPolicyMotorDetails = (
  options?: QueryHookOptions<GetPolicyMotorDetailsResponse, PolicyIDVariables>
) =>
  useApiQuery<GetPolicyMotorDetailsResponse, PolicyIDVariables>(
    getPolicyMotor,
    'getPolicyMotor',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    { withLoader: false }
  );

const useGetMotorLovs = (
  options?: QueryHookOptions<GetMotorDetailsLovsResponse>
) =>
  useApiLazyQuery<GetMotorDetailsLovsResponse>(
    getMotorLovs,
    'getMotorLovs',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetMotorModelLovs = (
  options?: QueryHookOptions<
    GetMotorModelLovsResponse,
    GetMotorModelsDetailsLovsVariables
  >
) =>
  useApiLazyQuery<
    GetMotorModelLovsResponse,
    GetMotorModelsDetailsLovsVariables
  >(
    getMotorModelLovs,
    'getMotorModelLovs',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useValidateVignette = (
  options?: QueryHookOptions<
    ValidateVignetteResponse,
    ValidateVignetteVariables
  >
) =>
  useApiLazyQuery<ValidateVignetteResponse, ValidateVignetteVariables>(
    validateVignette,
    'validateVignette',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useValidateVignetteExist = (
  options?: QueryHookOptions<
    ValidateVignetteExistResponse,
    ValidateVignetteExistVariables
  >
) =>
  useApiLazyQuery<
    ValidateVignetteExistResponse,
    ValidateVignetteExistVariables
  >(
    validateVignetteExist,
    'validateVignetteExist',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );
const useInitializePolicyCorrection = () =>
  useApiMutation<InitializePolicyCorrectionResponse, PolicyIdVariables>(
    initializePolicyCorrection,
    'initializePolicyCorrection',
    {
      withLoader: true, // show your global loader
      successMsg: 'Policy correction initialized'
    },
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetPolicyPremiumDetails = (
  options?: QueryHookOptions<
    GetPolicyPremiumDetailsResponse,
    GetPolicyPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetPolicyPremiumDetailsResponse,
    GetPolicyPremiumDetailsVariables
  >(
    getPolicyPremiumDetails,
    'getPolicyPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetPolicyTravelPremiumDetails = (
  options?: QueryHookOptions<
    GetPolicyTravelPremiumDetailsResponse,
    GetPolicyPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetPolicyTravelPremiumDetailsResponse,
    GetPolicyPremiumDetailsVariables
  >(
    getPolicyTravelPremiumDetails,
    'getPolicyTravelPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetPolicyGroupMedicalCertificates = (
  options?: QueryHookOptions<
    GetPolicyGroupMedicalCertificatesResponse,
    GetPolicyGroupMedicalCertificatesVariables
  >
) =>
  useApiLazyQuery<
    GetPolicyGroupMedicalCertificatesResponse,
    GetPolicyGroupMedicalCertificatesVariables
  >(
    getPolicyGroupMedicalCertificates,
    'getPolicyGroupMedicalCertificates',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetPolicyFleetCertificates = (
  options?: QueryHookOptions<
    GetPolicyFleetCertificatesResponse,
    GetPolicyFleetCertificatesVariables
  >
) =>
  useApiLazyQuery<
    GetPolicyFleetCertificatesResponse,
    GetPolicyFleetCertificatesVariables
  >(
    getPolicyFleetCertificates,
    'getPolicyFleetCertificates',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetPolicyFleetMarineCertificates = (
  options?: QueryHookOptions<
    GetPolicyFleetMarineCertificatesResponse,
    GetPolicyFleetMarineCertificatesVariables
  >
) =>
  useApiLazyQuery<
    GetPolicyFleetMarineCertificatesResponse,
    GetPolicyFleetMarineCertificatesVariables
  >(
    getPolicyFleetMarineCertificates,
    'getPolicyFleetMarineCertificates',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useValidateFleetCertificate = (
  policyID: string,
  lineCode: string,
  setPopUpOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorsLogs: React.Dispatch<React.SetStateAction<ErrorLogs[]>>,
  setIsSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setUploadedDocument: React.Dispatch<React.SetStateAction<IDocument>>
) => {
  const apiUploader = useApiUploader<
    UploadFleetListResponse,
    PolicyIDVariables
  >();
  // const isFleetLine = [4, 51].includes(Number(lineCode));
  const isMarine = getGenericLine(lineCode) === GenericLines.MARINE;

  // Determine which mutation and response path to use
  const mutation = isMarine ? uploadMarineFleetList : uploadFleetList;
  const Key = isMarine ? 'uploadMarineFleetList' : 'uploadFleetList';

  return async (file: File) =>
    apiUploader(mutation, { policyID }, file, {
      key: Key,
      successMessage: 'File was validated successfully!',
      successCondition: (body) => body.data.production[Key].length === 0,
      onErrorCallback: (body) => {
        if (body.data) {
          setErrorsLogs(body.data?.production?.[Key]);
          setIsSubmitButtonDisabled(true);
          setUploadedDocument(undefined);
          setPopUpOpen(true);
        }
      }
    });
};

const useUploadFleetCertificate = (policyID: string, lineCode: string) => {
  const apiUploader = useApiUploader<
    UploadFleetListResponse,
    PolicyIDVariables
  >();
  const isMarine = getGenericLine(lineCode) === GenericLines.MARINE;

  // Determine which mutation and response path to use
  const mutation = isMarine ? addMarineCertificate : addCertificate;
  const Key = isMarine ? 'addMarineCertificate' : 'addCertificate';
  return async (file: File) =>
    apiUploader(mutation, { policyID }, file, {
      key: Key,
      successMessage: 'File uploaded successfully!',
      refetchQueries: [
        isMarine ? getPolicyFleetMarineCertificates : getPolicyFleetCertificates
      ]
    });
};

const useGetPoliciesForRenewals = (
  options?: QueryHookOptions<
    GetPoliciesForRenewalListResponse,
    GetPoliciesForRenewalListVariables
  >
) =>
  useApiLazyQuery<
    GetPoliciesForRenewalListResponse,
    GetPoliciesForRenewalListVariables
  >(
    searchPoliciesForRenewals,
    'searchPoliciesForRenewals',
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: false,
      ...(options ?? {})
    },
    { withLoader: false }
  );

const useDownloadRenewalListOfVehicles = () => {
  const [downloadApi] = useApiLazyQuery<
    DownloadRenewalListOfVehiclesResponse,
    PolicyIDVariables
  >(
    downloadRenewalListOfVehicles,
    'downloadRenewalListOfVehicles',
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

  const downloadPolicyFunction = async (
    variables: PolicyIDVariables
  ): Promise<void | null> => {
    try {
      const result = await downloadApi({
        variables
      });

      const data = result.data?.production?.downloadRenewalListOfVehicles;
      if (!data) return null;

      DataService.downloadBase64File(
        data.content,
        data.contentType,
        data.fileName
      );
    } catch (err) {
      console.error('Error downloading Renewal List of Vehicles', err);
      return null;
    }
  };

  return downloadPolicyFunction;
};

const useDownloadRenewalListOfMarines = () => {
  const [downloadApi] = useApiLazyQuery<
    DownloadRenewalListOfMarinesResponse,
    PolicyIDVariables
  >(
    downloadRenewalListOfMarines,
    'downloadRenewalListOfMarines',
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

  const downloadPolicyFunction = async (
    variables: PolicyIDVariables
  ): Promise<void | null> => {
    try {
      const result = await downloadApi({
        variables
      });

      const data = result.data?.production?.downloadRenewalListOfMarines;
      if (!data) return null;

      DataService.downloadBase64File(
        data.content,
        data.contentType,
        data.fileName
      );
    } catch (err) {
      console.error('Error downloading Renewal List of Marines', err);
      return null;
    }
  };

  return downloadPolicyFunction;
};

export {
  useGetAdditionalPolicyDocuments,
  useGetGeoLocations,
  useGetMotorLovs,
  useGetMotorModelLovs,
  useGetPolicies,
  useGetPolicyAssignedLines,
  useGetPolicyBBBDetails,
  useGetPolicyBusinessPartners,
  useGetPolicyById,
  useGetPolicyCovers,
  useGetPolicyDocuments,
  useGetPolicyExpatDetails,
  useGetPolicyFilters,
  useGetPolicyGroupMedicalCertificates,
  useGetPolicyFleetCertificates,
  useGetPolicyMarineDetails,
  useGetPolicyMarineHullDetails,
  useGetPolicyMedicalDetails,
  useGetPolicyMotorDetails,
  useGetPolicyPayments,
  useGetCertificatePayments,
  useGetPolicyPersons,
  useGetPolicyPremiumDetails,
  useGetPolicyPropertyDescriptionDetails,
  useGetPolicyQuoteById,
  useGetPolicyRiskDescriptionDetails,
  useGetPolicyTravelDetails,
  useGetPolicyTravelPremiumDetails,
  useGetPropertyDescriptionLovs,
  useInitializePolicyCorrection,
  useRegeneratePolicyDocuments,
  useValidateVignette,
  useValidateVignetteExist,
  useValidateFleetCertificate,
  useUploadFleetCertificate,
  useGetPoliciesForRenewals,
  useDownloadRenewalListOfVehicles,
  useGetPolicyFleetMarineCertificates,
  useDownloadRenewalListOfMarines
};
